import React, { FC, useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ar, enUS } from "date-fns/locale";
import { useRouter } from "next/router";
import styles from "../DateRangePicker/DateRangePicker.module.scss";

interface SingleDatePickerProps {
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  selectedDate: dayjs.Dayjs | null;
  className?: string;
  onChange: (date: dayjs.Dayjs | null) => void;
  dropdownIsOpen: boolean;
  isBannerSearch?:boolean;
}

const SingleDatePickerComp: FC<SingleDatePickerProps> = ({
  minDate,
  maxDate,
  selectedDate,
  className,
  onChange,
  dropdownIsOpen,
  isBannerSearch
}) => {
  const router = useRouter();
  const locale = router.locale ?? "en";
  const today = dayjs().startOf("day");
  const [open, setOpen] = useState(dropdownIsOpen);
  const [inputValue, setInputValue] = useState(
    selectedDate ? selectedDate.locale(locale).format("DD MMM, YYYY") : ""
  );
  const inputRef = useRef<HTMLDivElement | null>(null);
  const calendarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setInputValue(selectedDate ? selectedDate.locale(locale).format("ddd, DD MMM") : "");
  }, [selectedDate, locale]);

  useEffect(() => {
    setOpen(dropdownIsOpen);
  }, [dropdownIsOpen]);

  const handleInputClick = () => {
    setOpen((prev) => !prev);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      calendarRef.current &&
      inputRef.current &&
      !calendarRef.current.contains(event.target as Node) &&
      !inputRef.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      data-testid="SingleDatePicker"
      onClick={handleInputClick}
      ref={inputRef}
      role="button"
      tabIndex={0}
      aria-label="Open date picker"
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          handleInputClick();
        }
      }}
    >
      <div className={styles.content}>
        {!isBannerSearch && 
        <input
        id="singleDate"
        readOnly
        value={inputValue}
        placeholder="Select date"
        className={`${styles.input} outline-none ${className}  min-w-[48px] min-h-6 max-c-md:!text-sm max-c-md:!leading-5`}
        />
      }
        {open && (
          <div
            className={styles.calendar}
            onClick={(e) => e.stopPropagation()}
            ref={calendarRef}
          >
            <div
              className="h-[14px] w-[14px] border-t border-l z-30 border-[#E5E4E3] absolute top-[-6px] ltr:left-[20px] rtl:right-[20px] bg-white rotate-45"
            />
            <DatePicker
              locale={locale === "ar" ? ar : enUS}
              selected={selectedDate ? selectedDate.toDate() : null}
              onChange={(date) => onChange(date ? dayjs(date) : null)}
              inline
              minDate={minDate ? minDate.toDate() : today.toDate()}
              maxDate={maxDate ? maxDate.toDate() : today.add(1, "year").toDate()}
              calendarClassName="z-20"
                monthsShown={2}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleDatePickerComp;
